import * as React from 'react'
import { Check } from '@phosphor-icons/react'
import { Badge, type BadgeColor } from '../core/badge'
import {
  Card,
  CardHeader,
  CardTitle,
  CardAction,
  CardContent,
  CardFooter,
} from '../core/card'
import { Skeleton } from '../core/skeleton'
import {
  AiMark,
  EntityLinkList,
  type ActionCardEntity,
  type ActionCardSize,
} from './action-center-card'

/**
 * CompletedActionCard — the "done" counterpart to {@link ActionCenterCard}, for
 * the {@link ViewCompletedPanel} history. Same anatomy (title + AI mark + status
 * `Badge`, a body, then a bordered footer of linked records) but:
 *  - the badge is an outcome (`Applied`, `Auto-resolved`), green by default;
 *  - the body is the **checklist of what was done** (`steps`, green checks), a
 *    short narrative (`description`), or a `loading` skeleton while the detail
 *    streams in;
 *  - the footer trails a completion `time` ("Just now", "21h ago") instead of
 *    View / Dismiss actions.
 *
 *   <CompletedActionCard
 *     title="Advance Coastal Fresh, pilot wrapped"
 *     status={{ label: 'Applied' }}
 *     steps={['Updated deal stage', 'Updated close date', 'Added deal note']}
 *     entities={[{ icon: <Handshake />, name: 'Coastal Fresh…', href: '/deals/1' }]}
 *     time="Just now"
 *   />
 */

export interface CompletedActionCardProps {
  /** What was completed (the original recommendation headline). */
  title: React.ReactNode
  /** Outcome badge — `Applied`, `Auto-resolved`, … Defaults to green (`success`). */
  status: { label: React.ReactNode; color?: BadgeColor }
  /** Show the AI mark. Default `true`. */
  ai?: boolean
  /** The applied sub-actions, rendered as a green-checked checklist. */
  steps?: React.ReactNode[]
  /** A narrative body — used when there's no `steps` list (e.g. auto-resolved). */
  description?: React.ReactNode
  /** Render a skeleton body while the "what was done" detail loads. */
  loading?: boolean
  /** Linked records shown in the footer (deal / company / contact …). */
  entities?: ActionCardEntity[]
  /** Completion time, trailing the footer (e.g. "Just now", "21h ago"). */
  time?: React.ReactNode
  /** Scales the whole card. Default `md`. */
  size?: ActionCardSize
  className?: string
}

export const CompletedActionCard = React.forwardRef<
  HTMLDivElement,
  CompletedActionCardProps
>(
  (
    {
      title,
      status,
      ai = true,
      steps,
      description,
      loading = false,
      entities,
      time,
      size = 'md',
      className,
    },
    ref
  ) => {
    const hasEntities = entities != null && entities.length > 0
    const hasSteps = steps != null && steps.length > 0
    const hasBody = loading || hasSteps || description != null
    return (
      <Card ref={ref} size={size} className={className}>
        <CardHeader className={size === 'sm' ? 'pb-1.5' : 'pb-2'}>
          <CardTitle>{title}</CardTitle>
          <CardAction>
            <div className="flex items-center gap-2">
              {ai && <AiMark />}
              <Badge color={status.color ?? 'success'}>{status.label}</Badge>
            </div>
          </CardAction>
        </CardHeader>
        {hasBody && (
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-3.5 w-full" />
                <Skeleton className="h-3.5 w-4/5" />
                <Skeleton className="h-3.5 w-3/5" />
              </div>
            ) : hasSteps ? (
              <ul className="space-y-1">
                {steps!.map((step, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 text-sm text-fbui-gray-500"
                  >
                    <Check
                      aria-hidden="true"
                      className="size-4 shrink-0 text-fbui-green-600"
                    />
                    <span className="min-w-0">{step}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-fbui-gray-500">{description}</p>
            )}
          </CardContent>
        )}
        {/* Same bordered action bar as the active card — linked records left,
            completion time right (via `ml-auto`). */}
        <CardFooter bordered className="flex-wrap gap-x-4 gap-y-2">
          {hasEntities && <EntityLinkList entities={entities} size={size} />}
          {time != null && (
            <span className="ml-auto shrink-0 text-xs text-fbui-gray-400">
              {time}
            </span>
          )}
        </CardFooter>
      </Card>
    )
  }
)
CompletedActionCard.displayName = 'CompletedActionCard'
